import { h, type App, type Component } from 'vue';
import { Wormhole } from 'portal-vue';
import { uniqueID } from './unique-id';
import { getModalComponent } from './modal-registry';

const MODAL_TARGET = 'imodal';

interface ModalInstance {
  name: string;
  componentName: string;
  resolve: (result: any) => void;
  reject: (error: any) => void;
}

export class ModalManager {
  private instances: ModalInstance[] = [];
  private app: App | null = null;

  setApp(app: App) {
    this.app = app;
  }

  show(opts: { name: string; props?: Record<string, any> }): Promise<any> {
    const id = `modal-${opts.name}-${uniqueID(8)}`;

    const modalProps: Record<string, any> = {
      key: id,
      modalName: id,
      modal: true,
      ...(opts.props ?? {}),
      componentInitErrorCallback: () => this.close(id, { cancel: true }),
    };

    return new Promise((resolve, reject) => {
      try {
        const component = this.resolveComponent(opts.name);

        const depth = this.instances.length + 1;

        const vnode = h(component as Component, {
          ...modalProps,
          class: { 'modal-in-portal': true },
          style: { '--itp-modal-depth': String(Math.min(depth, 5)) },
          onClose: (result?: any) => this.close(id, result ?? { close: true }),
          onOk: (result?: any) =>
            this.close(id, result ?? { ok: true, value: true }),
          onCancel: (result?: any) =>
            this.close(id, result ?? { cancel: true, value: false }),
        });

        if (this.app) {
          vnode.appContext = this.app._context;
        }

        Wormhole.open({
          to: MODAL_TARGET,
          from: id,
          content: () => [vnode],
          order: depth,
        });

        this.instances.push({
          name: id,
          componentName: opts.name,
          resolve,
          reject,
        });
      } catch (error) {
        console.error('Modal show error:', error);
        reject(error);
      }
    });
  }

  close(idOrName: string, result?: any) {
    const instance =
      this.instances.find((m) => m.name === idOrName) ||
      [...this.instances].reverse().find((m) => m.componentName === idOrName);

    if (!instance) {
      return;
    }

    Wormhole.close({
      from: instance.name,
      to: MODAL_TARGET,
    });

    this.instances = this.instances.filter((m) => m.name !== instance.name);
    instance.resolve(result);
  }

  closeTop(result?: any) {
    const instance = this.instances[this.instances.length - 1];
    if (!instance) {
      return;
    }
    this.close(instance.name, result);
  }

  errorCaptured(_err: Error) {
    const instance = this.instances[this.instances.length - 1];
    if (!instance) {
      return;
    }
    this.close(instance.name, { error: _err });
  }

  private resolveComponent(name: string): Component | string {
    const registryComponent = getModalComponent(name);
    if (registryComponent) {
      return registryComponent;
    }

    if (this.app) {
      const component = this.app.component(name);
      if (component) {
        return component;
      }
    }

    console.warn(`Modal: Component "${name}" not found`);
    return name;
  }
}
